import { useState, useMemo } from 'react';
import { Rocket, Users, CircleHelp, Clock, DollarSign, TrendingUp, BarChart, Search, HelpCircle, MessageCircle, ExternalLink, Settings, ShieldCheck, FileText, ArrowRight, Calendar, Star, FileStack, Network, Split, UserCog, UserPlus, CreditCard, DoorOpen, Mail, User, UserRoundCog } from 'lucide-react';
import { useGetPublicArticlesQuery, useGetModulesQuery, Article } from '../../store/apis/helpCenter.api';
import { markdownToPlainText } from '../../utils/helpCenterMarkdown';
import { HelpCategories } from './HelpCategories';
import { HelpArticle } from './HelpArticle';
import { ArticleList } from './ArticleList';
import { HelpContactForm } from './HelpContactForm';
import { Button, SearchInput, Select } from '../common';
import FilterWrapper from '../common/FilterWrapper';
import { CategoryGridSkeleton, SearchResultSkeleton } from './Skeleton';

const iconMap: Record<string, React.ReactNode> = {
    Rocket: <Rocket size={20} />,
    Users: <Users size={20} />,
    Clock: <Clock size={20} />,
    DollarSign: <DollarSign size={20} />,
    TrendingUp: <TrendingUp size={20} />,
    BarChart: <BarChart size={20} />,
    Settings: <Settings size={20} />,
    ShieldCheck: <ShieldCheck size={20} />,
    FileText: <FileText size={20} />,
    Calendar: <Calendar size={20} />,
    Star: <Star size={20} />,
    FileStack: <FileStack size={20} />,
    Network: <Network size={20} />,
    Split: <Split size={20} />,
    UserCog: <UserCog size={20} />,
    UserPlus: <UserPlus size={20} />,
    CreditCard: <CreditCard size={20} />,
    DoorOpen: <DoorOpen size={20} />,
    Mail: <Mail size={20} />,
    User: <User size={20} />,
    UserRoundCog: <UserRoundCog size={20} />,
    HandHelping: <CircleHelp size={20} />,
};

const moduleIconMapping: Record<string, string> = {
    RECRUITMENT: "Users",
    LEAVE: "Calendar",
    ATTENDANCE: "Clock",
    LEAVE_ATTENDANCE: "Calendar",
    PAYROLL: "CreditCard",
    PERFORMANCE: "Star",
    REPORTS: "FileText",
    ADMIN_REPORTS: "FileUser",
    EMPLOYEE_MANAGEMENT: "Users",
    ADMIN_EMPLOYEE_MANAGEMENT: "Users",
    EMPLOYEE_PROFILE: "User",
    USER_EMPLOYEE_PROFILE: "User",
    TIMESHEET: "Clock",
    ADMIN_CONFIG: "Settings",
    BILLING_LICENSE: "CreditCard",
    SURVEYS: "FileStack",
    RESOURCE_ALLOCATION: "Network",
    WORKFLOW: "Split",
    ORGANIZATION_ONBOARDING: "UserCog",
    REFERRAL: "UserPlus",
    CONFIG_MANAGEMENT: "Settings",
    SEPARATION: "DoorOpen",
    TERMINATION: "DoorOpen",
    EMAIL_MANAGEMENT: "Mail",
    ACCESS_MANAGEMENT: "UserRoundCog",
    HELP_CENTER: "HandHelping",
};

export function HelpCenterMain() {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactFormProps, setContactFormProps] = useState<{
        moduleCode?: string;
        articleId?: string;
        isLocked?: boolean;
    }>({});

    const { data: publicArticlesData, isLoading: articlesLoading } = useGetPublicArticlesQuery();
    const { data: allModules = [] } = useGetModulesQuery();

    // Flatten all articles from all modules into a single searchable array
    const allArticles = useMemo(() => {
        if (!publicArticlesData) return [];
        return Object.values(publicArticlesData).flat();
    }, [publicArticlesData]);

    // Perform local search filtering
    const searchResults = useMemo(() => {
        if (searchQuery.length < 2) return [];
        const query = searchQuery.toLowerCase();
        return allArticles.filter(article =>
            article.title.toLowerCase().includes(query) ||
            article.content.toLowerCase().includes(query)
        ).slice(0, 10);
    }, [allArticles, searchQuery]);

    const categories = useMemo(() => {
        return Object.entries(publicArticlesData || {}).map(([moduleCode, articles]) => {
            // Find module info from allModules to get the correct display name
            const moduleInfo = allModules.find(m => m.code === moduleCode);

            const name = moduleInfo?.name || moduleCode
                .split('_')
                .map(word => word.charAt(0) + word.slice(1).toLowerCase())
                .join(' ');

            return {
                id: moduleCode,
                name,
                description: `Documentation and guides for the ${name.toLowerCase()}.`,
                icon: moduleIconMapping[moduleCode] || "Rocket",
                articleCount: articles.length,
                topArticles: articles.slice(0, 3).map(a => ({ id: a._id, title: a.title })),
            };
        });
    }, [publicArticlesData, allModules]);


    const handleBack = () => {
        setSelectedArticleId(null);
        setSelectedCategoryId(null);
        setShowContactForm(false);
        setContactFormProps({});
    };

    if (showContactForm) {
        return (
            <HelpContactForm
                onBack={() => {
                    setShowContactForm(false);
                    setContactFormProps({});
                }}
                initialModuleCode={contactFormProps.moduleCode}
                initialArticleId={contactFormProps.articleId}
                isLocked={contactFormProps.isLocked}
            />
        );
    }

    const handleArticleBack = () => {
        setSelectedArticleId(null);
    };

    if (selectedArticleId) {
        return (
            <HelpArticle
                articleId={selectedArticleId}
                onBack={handleArticleBack}
                onContactSupport={(moduleCode, articleId) => {
                    setContactFormProps({ moduleCode, articleId, isLocked: true });
                    setShowContactForm(true);
                }}
            />
        );
    }

    if (selectedCategoryId) {
        return (
            <ArticleList
                categoryId={selectedCategoryId}
                onBack={handleBack}
                onArticleClick={(articleId) => setSelectedArticleId(articleId)}
            />
        );
    }

    return (
        <div className="mx-auto p-4 sm:p-6 lg:p-8 bg-surface-muted min-h-full">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                <div>
                    <h1 className="text-3xl md:text-4xl text-slate-900 font-bold tracking-tight">
                        Help Center
                    </h1>
                </div>

                <div className="relative w-full md:w-96">
                    <SearchInput
                        placeholder="Search help articles"
                        value={searchQuery}
                        onChange={(val) => setSearchQuery(val)}
                        className="w-full"
                    />

                    {/* Search Results Dropdown */}
                    {searchQuery.length >= 2 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-50 text-left">
                            {articlesLoading ? (
                                <SearchResultSkeleton />
                            ) : searchResults && searchResults.length > 0 ? (
                                <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-50">
                                    {searchResults.map((article: Article) => (
                                        <button
                                            key={article._id}
                                            onClick={() => {
                                                setSelectedArticleId(article._id);
                                                setSearchQuery('');
                                            }}
                                            className="w-full p-5 text-left hover:bg-slate-50 transition-colors group"
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <h3 className="font-bold text-slate-900 group-hover:text-primary transition-colors">{article.title}</h3>
                                                <ExternalLink size={14} className="text-slate-300 group-hover:text-primary transition-colors" />
                                            </div>
                                            <p className="text-xs text-slate-500 line-clamp-1 leading-relaxed">
                                                {markdownToPlainText(article.content, 150)}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-10 text-center">
                                    <p className="text-slate-500 font-medium text-sm">No results found for "{searchQuery}"</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="space-y-8">
                {/* Categories Section */}
                {articlesLoading ? (
                    <CategoryGridSkeleton />
                ) : (
                    <HelpCategories
                        categories={categories as any}
                        iconMap={iconMap}
                        onCategoryClick={(categoryId) => setSelectedCategoryId(categoryId)}
                    />
                )}

                {/* Support Card */}
                <div className="pt-8">
                    <div className="w-full p-6 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-all relative overflow-hidden group flex flex-col md:flex-row items-center md:justify-between gap-6 shadow-sm">
                        <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 mb-1">Contact Support</h3>
                                <p className="text-sm text-slate-500 leading-relaxed max-w-2xl">
                                    Can't find what you're looking for? Reach out to our dedicated support team directly for help.
                                </p>
                            </div>
                        </div>
                        <Button
                            onClick={() => setShowContactForm(true)}
                            appearance="primary"
                            size="small"
                            className="w-full md:w-fit px-6"
                        >
                            Contact Support
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HelpCenterMain;
